import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  requestNotificationPermission, 
  getFCMToken, 
  onMessageListener, 
  deleteFCMToken,
  isNotificationSupported,
  getNotificationPermission
} from '../services/fcm.service';
import { showInfoToast, showSuccessToast, showErrorToast } from '../components/ToastNotifications';
import toast from 'react-hot-toast';

/**
 * Hook for managing push notifications
 */
export const useNotifications = () => {
  const { user } = useAuth();
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [isSupported, setIsSupported] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    const supported = isNotificationSupported();
    setIsSupported(supported);

    if (!supported) {
      console.log('Notifications not supported in this browser');
      return;
    }

    // Update permission state
    setPermission(getNotificationPermission());
  }, []);

  useEffect(() => {
    if (!user || !isSupported) return;

    const initializeNotifications = async () => {
      setIsInitializing(true);

      try {
        // Check current permission
        const currentPermission = getNotificationPermission();
        setPermission(currentPermission);

        if (currentPermission === 'granted') {
          // Get FCM token
          const fcmToken = await getFCMToken(user.uid);
          if (fcmToken) {
            setToken(fcmToken);
            console.log('Notifications initialized successfully');
          }
        } else if (currentPermission === 'default') {
          // Permission not yet requested - don't auto-request
          console.log('Notification permission not yet requested');
        }
      } catch (error) {
        console.error('Error initializing notifications:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeNotifications();

    // Listen for foreground messages
    const setupMessageListener = async () => {
      try {
        const payload = await onMessageListener();
        if (payload) {
          const notification = payload.notification;
          
          // Show toast notification
          toast.success(notification?.title || 'Nieuwe notificatie', {
            description: notification?.body,
            duration: 5000,
            icon: '🔔'
          });

          // Also show browser notification if permission granted
          if (Notification.permission === 'granted' && notification) {
            new Notification(notification.title || 'StudyStay', {
              body: notification.body,
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: payload.data?.type || 'default',
              data: payload.data,
              requireInteraction: payload.data?.requireInteraction === 'true'
            });
          }
        }
      } catch (error) {
        console.error('Error in message listener:', error);
      }
    };

    // Set up listener (this will keep listening)
    setupMessageListener();
  }, [user, isSupported]);

  /**
   * Request notification permission and initialize
   */
  const enableNotifications = async () => {
    if (!isSupported) {
      showErrorToast('Notificaties worden niet ondersteund in deze browser');
      return false;
    }

    try {
      const hasPermission = await requestNotificationPermission();
      setPermission(getNotificationPermission());

      if (hasPermission && user) {
        const fcmToken = await getFCMToken(user.uid);
        if (fcmToken) {
          setToken(fcmToken);
          showSuccessToast('Notificaties ingeschakeld!');
          return true;
        }
      } else {
        showInfoToast('Notificatie toestemming geweigerd');
        return false;
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      showErrorToast('Fout bij inschakelen notificaties');
      return false;
    }
  };

  /**
   * Disable notifications
   */
  const disableNotifications = async () => {
    if (user) {
      await deleteFCMToken(user.uid);
      setToken(null);
      setPermission(getNotificationPermission());
      showInfoToast('Notificaties uitgeschakeld');
    }
  };

  return {
    token,
    permission,
    isSupported,
    isInitializing,
    enableNotifications,
    disableNotifications
  };
};

